import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// AssistantWindow.tsx -- one floating chat window in the multi-window
// registry.
//
// Each instance is bound to a windowId; it reads its slice of state
// from the chat store and dispatches user actions back through store
// actions. Multiple AssistantWindows can be on screen at once -- they
// share nothing beyond project context (registered globally on the
// store) and the side-effect dispatcher (also store-global). Each
// window has its own session, messages, recipient, hint,
// currentFolder, and pose.
//
// Per design \u00a76.3:
//   - Default size ~480x640. Position cascades from the bottom-right
//     corner so successive windows don't pile on top of each other.
//   - Header: avatar + recipient-name chip; conversation title
//     (auto-generated, click to rename); project subtitle; History +
//     Minimize + Close controls.
//   - Body: scrollable MessageList (auto-scroll, user-scroll-back works).
//   - Footer: textarea grows up to 6 rows; Enter submits, Shift+Enter
//     newline; Send button disables while sending.
//   - Minimize collapses to a small pill (still per-window) at the
//     bottom-right strip; click pill to expand.
//   - Resize via SE handle; pose persisted per windowId in localStorage.
import { useCallback, useEffect, useRef, useState } from 'react';
import { MessageList } from './MessageList.js';
import { useChatStore, useChatWindow, useMinimizedIndex, useProjectContext, } from '../../hooks/useAgent.js';
import { RecipientHistoryPopover } from './RecipientHistoryPopover.js';
import { ChatActionsMenu } from './ChatActionsMenu.js';
import { ContentTypeChip } from './ContentTypeChip.js';
import { useAuth } from '../../hooks/useAuth.js';
const MIN_W = 360;
const MIN_H = 420;
function clampSize(w, h) {
    const maxW = Math.max(MIN_W, window.innerWidth - 48);
    const maxH = Math.max(MIN_H, window.innerHeight - 80);
    return {
        w: Math.max(MIN_W, Math.min(maxW, w)),
        h: Math.max(MIN_H, Math.min(maxH, h)),
    };
}
// Pill geometry for the minimized strip. Pills lay out from the
// bottom-right corner, growing leftward. When the row is full
// (would overflow window width minus a margin), wraps up to a row
// above with the same right-to-left flow.
const PILL_W = 220;
const PILL_H = 56;
const PILL_GAP = 8;
const PILL_BOTTOM_INSET = 24;
const PILL_RIGHT_INSET = 24;
const PILL_LEFT_GUTTER = 88; // sits clear of the MembersDock (64px) + 24px margin
function computePillPose(index) {
    if (typeof window === 'undefined') {
        return { right: PILL_RIGHT_INSET, bottom: PILL_BOTTOM_INSET };
    }
    const usableWidth = window.innerWidth - PILL_RIGHT_INSET - PILL_LEFT_GUTTER;
    const perRow = Math.max(1, Math.floor((usableWidth + PILL_GAP) / (PILL_W + PILL_GAP)));
    const row = Math.floor(index / perRow);
    const col = index % perRow; // 0 = rightmost
    return {
        right: PILL_RIGHT_INSET + col * (PILL_W + PILL_GAP),
        bottom: PILL_BOTTOM_INSET + row * (PILL_H + PILL_GAP),
    };
}
export function AssistantWindow({ windowId }) {
    const win = useChatWindow(windowId);
    const { project, projectId } = useProjectContext();
    const { user } = useAuth();
    const viewerUserId = user?.userId ?? null;
    // -1 when this window isn't minimized; non-negative index when it is.
    const minimizedIndex = useMinimizedIndex(windowId);
    // Local input state (the only piece of UI state that doesn't belong
    // in the store). Hint + sending live on the window slice; just the
    // textarea draft is local.
    const [text, setText] = useState('');
    const taRef = useRef(null);
    // ---- recipient history popover ----
    // Triggered by an item in the ChatActionsMenu (not a dedicated header
    // button anymore, per user direction). The popover anchors to the
    // actions-menu trigger button so it pops in the same screen position
    // a user would expect after picking "Past Conversations" from the menu.
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyAnchor, setHistoryAnchor] = useState(null);
    // ---- chat actions menu ("+" button on the header) ----
    // Replaces the standalone 📜 history button. Per-window menu of
    // actions; today's only item is "Past Conversations" but the menu
    // is the home for future per-window actions (e.g. "Add attachment"
    // when I17 lands).
    const [actionsOpen, setActionsOpen] = useState(false);
    const [actionsAnchor, setActionsAnchor] = useState(null);
    const actionsBtnRef = useRef(null);
    const onToggleActions = useCallback(() => {
        const btn = actionsBtnRef.current;
        if (btn) {
            const r = btn.getBoundingClientRect();
            setActionsAnchor({ x: r.x, y: r.y, width: r.width, height: r.height });
        }
        setActionsOpen((o) => !o);
    }, []);
    // When a menu item picks "Past Conversations" we swap the popover for
    // the history popover, anchored to the same trigger button.
    const onOpenHistoryFromMenu = useCallback(() => {
        const btn = actionsBtnRef.current;
        if (btn) {
            const r = btn.getBoundingClientRect();
            setHistoryAnchor({ x: r.x, y: r.y, width: r.width, height: r.height });
        }
        setHistoryOpen(true);
    }, []);
    // ---- inline-rename ----
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');
    const titleInputRef = useRef(null);
    const dragStateRef = useRef(null);
    const resizeStateRef = useRef(null);
    const lastResizedSizeRef = useRef(null);
    // Auto-grow textarea up to 6 rows.
    useEffect(() => {
        const ta = taRef.current;
        if (!ta)
            return;
        ta.style.height = 'auto';
        const lineH = 20;
        const max = lineH * 6 + 16;
        ta.style.height = `${Math.min(max, ta.scrollHeight)}px`;
    }, [text]);
    // Re-clamp pose on viewport resize so the window doesn't end up
    // bigger than the screen.
    useEffect(() => {
        const onResize = () => {
            if (!win)
                return;
            const c = clampSize(win.pose.w, win.pose.h);
            if (c.w !== win.pose.w || c.h !== win.pose.h) {
                useChatStore.getState().setWindowPose(windowId, c);
            }
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [windowId, win]);
    if (!win || !project)
        return null;
    // ---- derived header bits from the discriminated recipient ----
    const recipient = win.recipient;
    const recipientName = recipient.kind === 'fdn'
        ? 'FDN Assistant'
        : recipient.kind === 'coworker'
            ? recipient.name
            : recipient.name;
    const recipientGlyph = recipient.kind === 'fdn'
        ? '\u2728'
        : recipient.kind === 'coworker'
            ? (recipient.avatarEmoji ?? '\uD83E\uDD16')
            : (recipient.avatarEmoji ?? '\uD83D\uDC64');
    const recipientLabel = recipient.kind === 'fdn'
        ? 'FDN Assistant'
        : recipient.kind === 'coworker'
            ? `Co-worker: ${recipientName}`
            : `Member: ${recipientName}`;
    // Drag the window via the header. Updates pose on the store on
    // every mouse-move so other components (minimized strip, focus
    // stacking) see live position. Persists via setWindowPose's
    // localStorage write.
    const onHeaderMouseDown = (e) => {
        if (e.target.closest('button'))
            return;
        if (e.target.closest('input'))
            return;
        const rect = e.currentTarget.parentElement.getBoundingClientRect();
        dragStateRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            origX: rect.left,
            origY: rect.top,
            w: rect.width,
        };
        useChatStore.getState().focusWindow(windowId);
        const onMove = (ev) => {
            const s = dragStateRef.current;
            if (!s)
                return;
            const x = Math.max(8, Math.min(window.innerWidth - s.w - 8, s.origX + (ev.clientX - s.startX)));
            const y = Math.max(8, Math.min(window.innerHeight - 80, s.origY + (ev.clientY - s.startY)));
            useChatStore.getState().setWindowPose(windowId, { x, y });
        };
        const onUp = () => {
            dragStateRef.current = null;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };
    const onResizeHandleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        resizeStateRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            origW: win.pose.w,
            origH: win.pose.h,
        };
        lastResizedSizeRef.current = { w: win.pose.w, h: win.pose.h };
        const onMove = (ev) => {
            const s = resizeStateRef.current;
            if (!s)
                return;
            const next = clampSize(s.origW + (ev.clientX - s.startX), s.origH + (ev.clientY - s.startY));
            lastResizedSizeRef.current = next;
            useChatStore.getState().setWindowPose(windowId, next);
        };
        const onUp = () => {
            resizeStateRef.current = null;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };
    const startEditTitle = () => {
        setTitleDraft(win.sessionTitle ?? '');
        setEditingTitle(true);
        requestAnimationFrame(() => titleInputRef.current?.select());
    };
    const commitTitle = async () => {
        const next = titleDraft.trim();
        setEditingTitle(false);
        if (!next || next === (win.sessionTitle ?? ''))
            return;
        if (!win.sessionId)
            return; // can't rename a draft window
        try {
            await useChatStore.getState().renameWindow(windowId, next);
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[AssistantWindow] rename failed:', err);
        }
    };
    const cancelEditTitle = () => {
        setEditingTitle(false);
        setTitleDraft('');
    };
    const onSubmit = async () => {
        const v = text.trim();
        if (!v)
            return;
        // For LLM-assistant sessions, block a new submit while a
        // previous turn is still streaming. Industry pattern: the
        // user can keep TYPING (textarea stays editable) but the
        // Send button morphs into Stop until the stream finishes.
        // The textarea's Enter handler also blocks here so a
        // re-press of Enter mid-stream doesn't accidentally
        // double-send.
        if (win.sending)
            return;
        setText('');
        await useChatStore.getState().send(windowId, v, viewerUserId);
    };
    const onStop = () => {
        useChatStore.getState().stop(windowId);
    };
    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            // Don't auto-fire onSubmit while a turn is streaming;
            // the user can still TYPE during the stream (the textarea
            // is no longer disabled) but Enter is a no-op until the
            // stream completes OR they explicitly click Stop. Matches
            // ChatGPT / Claude / Gemini composer behavior.
            if (win.sending)
                return;
            void onSubmit();
        }
    };
    // Minimized: render the pill instead of the full window. Pills lay
    // out bottom-right -> bottom-left in a strip; wraps up to another
    // row when the row is full. Position computed from the
    // minimization index so each pill's geometry is independent of
    // store-wide re-renders.
    if (win.pose.minimized) {
        const idx = Math.max(0, minimizedIndex);
        const { right, bottom } = computePillPose(idx);
        const fullTitle = win.sessionTitle
            ? `${recipientName} -- ${win.sessionTitle}`
            : recipientName;
        return (_jsxs("button", { type: "button", className: "assistant-mini", onClick: () => useChatStore.getState().minimizeWindow(windowId, false), style: { right, bottom, width: PILL_W, height: PILL_H }, title: fullTitle, "aria-label": `Restore chat: ${fullTitle}`, children: [_jsx("span", { className: "assistant-mini-glyph", "aria-hidden": true, children: recipientGlyph }), _jsxs("div", { className: "assistant-mini-stack", children: [_jsx("span", { className: "assistant-mini-label", children: recipientName }), win.sessionTitle && (_jsx("span", { className: "assistant-mini-sub", title: win.sessionTitle, children: win.sessionTitle }))] }), win.sending && _jsx("span", { className: "assistant-mini-pulse" }), win.inlineUnread > 0 && (_jsx("span", { className: "assistant-mini-unread", "aria-hidden": true, children: win.inlineUnread > 99 ? '99+' : win.inlineUnread }))] }));
    }
    const style = {
        left: win.pose.x,
        top: win.pose.y,
        right: 'auto',
        bottom: 'auto',
        width: win.pose.w,
        height: win.pose.h,
        zIndex: 1000 + Math.floor(win.pose.focusedAt / 1000) % 1000000,
    };
    const isCoworker = recipient.kind === 'coworker';
    const isFdn = recipient.kind === 'fdn';
    return (_jsxs("div", { className: "assistant-window", style: style, role: "dialog", "aria-label": `Chat with ${recipientName}`, onMouseDown: () => useChatStore.getState().focusWindow(windowId), children: [_jsxs("div", { className: "assistant-header", onMouseDown: onHeaderMouseDown, children: [_jsx("span", { className: isFdn ? 'assistant-glyph' : 'assistant-glyph assistant-glyph-coworker', "aria-hidden": true, title: recipientLabel, children: recipientGlyph }), _jsx("span", { className: "assistant-identity", title: recipientLabel, children: recipientName }), _jsx("span", { className: "assistant-divider", "aria-hidden": true, children: '\u00B7' }), editingTitle ? (_jsx("input", { ref: titleInputRef, className: "assistant-title-input", value: titleDraft, onChange: (e) => setTitleDraft(e.target.value), onKeyDown: (e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                void commitTitle();
                            }
                            else if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelEditTitle();
                            }
                        }, onBlur: () => void commitTitle(), onMouseDown: (e) => e.stopPropagation(), placeholder: "Conversation title", maxLength: 120 })) : (_jsx("button", { type: "button", className: "assistant-title assistant-title-button", onClick: (e) => {
                            e.stopPropagation();
                            startEditTitle();
                        }, onMouseDown: (e) => e.stopPropagation(), title: "Click to rename this conversation", children: win.sessionTitle ?? 'New conversation' })), _jsx("span", { className: "assistant-spacer" }), _jsx("span", { className: "assistant-subtitle", title: project.projectId, children: project.name }), win.sessionId
                        && viewerUserId
                        && win.sessionOwnerId === viewerUserId
                        && (_jsx("button", { type: "button", className: "assistant-ctl", onClick: async (e) => {
                                e.stopPropagation();
                                const isLocked = !!(win.sessionParticipants && win.sessionParticipants.length > 0);
                                try {
                                    await useChatStore.getState().setSessionLock(windowId, !isLocked);
                                }
                                catch {
                                    /* setSessionLock already logged + reverted state */
                                }
                            }, onMouseDown: (e) => e.stopPropagation(), title: win.sessionParticipants && win.sessionParticipants.length > 0
                                ? 'This conversation is private to you. Click to share with the project.'
                                : 'This conversation is shared. Click to make it private.', children: win.sessionParticipants && win.sessionParticipants.length > 0
                                ? '\uD83D\uDD12'
                                : '\uD83D\uDD13' })), _jsx("button", { ref: actionsBtnRef, type: "button", className: "assistant-ctl assistant-ctl-plus", onClick: (e) => {
                            e.stopPropagation();
                            onToggleActions();
                        }, onMouseDown: (e) => e.stopPropagation(), title: "Conversation actions", "aria-expanded": actionsOpen, "aria-haspopup": "menu", children: '+' }), _jsx("button", { type: "button", className: "assistant-ctl", onClick: () => useChatStore.getState().minimizeWindow(windowId, true), title: "Minimize", children: '\u2014' }), _jsx("button", { type: "button", className: "assistant-ctl", onClick: () => useChatStore.getState().closeWindow(windowId), title: "Close", children: '\u2715' })] }), _jsx("div", { className: "assistant-body", children: _jsx(MessageList, { messages: win.messages, ...(projectId ? { projectId } : {}), ...(project.aidocsFolderId ? { aidocsFolderId: project.aidocsFolderId } : {}) }) }), win.hint && (_jsxs("div", { className: "assistant-hint-chip", children: [_jsx("span", { className: "hint-dot" }), " ", win.hint.label, _jsx("button", { type: "button", className: "hint-clear", onClick: () => useChatStore.getState().setHint(windowId, null), title: "Clear hint", children: '\u2715' })] })), _jsxs("div", { className: "assistant-footer", children: [_jsx("textarea", { ref: taRef, className: "assistant-input", value: text, onChange: (e) => setText(e.target.value), onKeyDown: onKeyDown, placeholder: win.hint?.placeholder
                            // Human-DM windows (recipient.kind === 'user') chat between
                            // two humans -- no LLM assistant, so the "Ask anything..."
                            // prompt is misleading. Show only the keyboard hint.
                            ?? (win.recipient?.kind === 'user'
                                ? 'Enter to send, Shift+Enter newline'
                                : 'Ask anything\u2026 (Enter to send, Shift+Enter newline)'), rows: 1 }), _jsx(ContentTypeChip, { windowId: windowId, disabled: false }), win.sending ? (_jsx("button", { type: "button", className: "assistant-send assistant-stop", onClick: onStop, title: "Stop generating", children: '\u25A0' })) : (_jsx("button", { type: "button", className: "assistant-send", disabled: !text.trim(), onClick: () => void onSubmit(), title: "Send (Enter)", children: '\u27A4' }))] }), _jsx("div", { className: "assistant-resize-handle", onMouseDown: onResizeHandleMouseDown, title: "Drag to resize", "aria-label": "Resize assistant window" }), projectId && recipient.kind !== 'fdn' && (
            // RecipientHistoryPopover works for any non-fdn recipient kind
            // (coworker / user / future group) by deriving its by-recipient
            // index key from the discriminated `recipient` prop.
            _jsx(RecipientHistoryPopover, { projectId: projectId, recipient: recipient, open: historyOpen, anchorRect: historyAnchor, onClose: () => setHistoryOpen(false) })), _jsx(ChatActionsMenu, { open: actionsOpen, anchorRect: actionsAnchor, onClose: () => setActionsOpen(false), items: [
                    {
                        // Project-wide conversations list (every topic across every
                        // recipient). User direction (May 2026): the "+" menu in a
                        // chat window is the entry point to JUMP to any other
                        // conversation, since FDN's mental model is
                        // topic-per-conversation -- people switch threads
                        // constantly. This sits ABOVE "Past Conversations" so the
                        // wider scope is the default-eye target.
                        label: 'Conversations',
                        onClick: () => useChatStore.getState().triggerOpenConversationsPanel(),
                    },
                    {
                        // Recipient-scoped history popover -- per-window
                        // affordance to revisit a prior topic with the SAME
                        // recipient. Hidden for the default-FDN session (no
                        // prior-recipient siblings to revisit).
                        label: 'Past Conversations',
                        onClick: onOpenHistoryFromMenu,
                        hidden: recipient.kind === 'fdn' || !projectId,
                    },
                ] })] }));
}
